import React, { useState, useMemo, useCallback } from 'react';
import { Grid, Button, Typography, Tooltip } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import ConfirmationDialogPanel from 'components/form/confirmation/confirmation-dialog-panel';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { PhoneUsage } from 'interfaces/redux/IPhone';
import { MobilePhoneSmsEnum } from 'constants/enums';
import { integrateUpdatedTasks, selectTaskKey, removeFCOrderTasks } from 'actions/action-tasks';
import { updateFillCycleDaysSupply, updateTherapyDaysSupply } from 'actions/action-therapies';
import { fetchNotes } from 'actions/action-notes';
import { ArborDate } from 'models/time/arbor-date';
import { SET_SHOW_SET_SELECTED_TASKS, FC } from 'constants/index';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { IPharmacy } from 'slices/pharmacies-slice';
import { IFillCoordinationTask } from 'interfaces/redux/task-types/IFillCoordinationTask';
import { SL_INCLUSION_STATUS } from 'constants/index';
import { RenderSimpleTherapyFields } from './render-therapy-fields';
import { SecureFCClient } from '../../../../clients/secure-fc-client';
import IconWithTooltip from '../../../../indicators/indicators';
import { getTaskKey } from '../../../patient/tasks-new/tasks-table/utils';

const RESEND_BUTTON_TEXT = 'Resend Link';
type Props = {
  tasks: IFillCoordinationTask[];
  allTasks: IFillCoordinationTask[];
  therapies: any;
  customerPharmacies: IPharmacy[];
  preferredRxDeliveryPharmacy: any;
  mostPrevelantDispensingPharmacyInBundle: any;
  containsAClinicAdministeredTherapy: boolean;
  patientIsSMSOptIn: boolean;
  includesOnlyFirstTimeFCs: boolean;
  secureLinkSendHistory: any;
  therapiesById: any;
  fcsById: any;
  isSecureLinkOptionEnabled: boolean;
  therapyIdsInBundle: number[];
  needInterpreter: boolean;
  formValues: any;
  classes: any;
  startingStatus: number;
  fieldSettings: any;
  fcInclusionStatus: Record<number, FcInclusionStatus> | undefined;
  isAnOrderTask: boolean;
  setFcInclusionStatus: any;
};

type FcInclusionStatus = {
  status: number;
  new_nbd: string | null;
  therapy_id: number | null;
  ds: number | null;
  fill_cycle_number: number | undefined;
};

export const SecureLinkForm = (props: Props) => {
  const {
    classes,
    tasks,
    allTasks,
    startingStatus,
    formValues,
    isAnOrderTask,
    therapyIdsInBundle,
    patientIsSMSOptIn,
    setFcInclusionStatus,
    customerPharmacies,
    preferredRxDeliveryPharmacy,
    mostPrevelantDispensingPharmacyInBundle,
    includesOnlyFirstTimeFCs,
    secureLinkSendHistory,
    therapiesById,
    fcsById,
    isSecureLinkOptionEnabled,
    needInterpreter,
    fieldSettings,
    fcInclusionStatus,
    containsAClinicAdministeredTherapy,
  } = props;
  const dispatch = useDispatch();
  const patient = useTypedSelector(state => state.patient);
  const patientTasks = useTypedSelector(state => state.tasks.data);
  const sortRankPhones = (patient.phones || []).sort((phonea, phoneb) => phonea.rank - phoneb.rank);
  const [resendLinkButtonText, setResendLinkButtonText] = useState(RESEND_BUTTON_TEXT);
  const [deactivateWarningConfig, setDeactivateWarningConfig] = useState({
    show: false,
    cancel: () => {},
    continue: () => {},
  });
  const smsPhoneNumber = sortRankPhones.find(
    phone =>
      !phone.deleted &&
      phone.use === PhoneUsage.Mobile &&
      phone.patient_choice === true &&
      phone.sms === MobilePhoneSmsEnum.OptIn,
  )?.value;
  const [fcssFormIsSubmitting, setFCssFormIsSubmitting] = useState(false);
  const [deactivateLinkButtonText, setDeactiveLinkButtonText] = useState('Deactivate Link');
  const [sendLinkButtonText, setSendLinkButtonText] = useState('Send Link to Patient');
  const [showSendSecureLinkWarning, setShowSendSecureLinkWarning] = useState<{
    show: boolean;
    showCancelButton?: boolean;
    dialogCancel?: () => void;
    dialogContinue?: () => void;
  }>({ show: false });
  const fcTaskIds = allTasks.map(task => task.id);
  const shouldShowSendSecureLinkWarning = therapyIdsInBundle.every(
    id => therapiesById?.[id]?.is_specialty == false && therapiesById?.[id]?.as_needed == true,
  );
  const renderDeactivateLinkConfirmation = () => {
    if (deactivateWarningConfig.show) {
      return (
        <ConfirmationDialogPanel
          open
          content={
            <Typography gutterBottom className={(props as any).classes.shippingAddressText}>
              This will deactivate the Secure Link for this FC and all FCs in the same bundle.
            </Typography>
          }
          showCancelButton
          cancelText="Cancel"
          continueText="Continue"
          onCancel={deactivateWarningConfig.cancel}
          onContinue={deactivateWarningConfig.continue}
        />
      );
    }
    return null;
  };

  const displaySendSecureLinkWarning = async () => {
    return new Promise(fn_continue => {
      setShowSendSecureLinkWarning(() => ({
        show: true,
        dialogCancel: () => {
          setShowSendSecureLinkWarning({ show: false });
          fn_continue(false);
        },
        dialogContinue: () => {
          setShowSendSecureLinkWarning({ show: false });
          fn_continue(true);
        },
      }));
    });
  };
  const secureLinkSenderId = allTasks?.find(
    task => task.secure_link_sent_by_user_id,
  )?.secure_link_sent_by_user_id;
  const secureFCLinkSender = useTypedSelector(state =>
    secureLinkSenderId
      ? state.lookups.users.find(user => user.id === secureLinkSenderId)?.display_name
      : '',
  );
  async function handleSendSecureLink() {
    if (shouldShowSendSecureLinkWarning) {
      const sendLink = await displaySendSecureLinkWarning();
      if (!sendLink) {
        return;
      }
    }
    setFCssFormIsSubmitting(true);
    setSendLinkButtonText('...Sending Link');
    const included_fc_task_ids = fcTaskIds.filter(
      id => fcInclusionStatus?.[id]?.status === SL_INCLUSION_STATUS.INCLUDED_1,
    );
    const removed_fc_task_info = fcInclusionStatus
      ? Object.entries(fcInclusionStatus)
          .filter(([_, statusInfo]) => statusInfo.status > SL_INCLUSION_STATUS.INCLUDED_1)
          .map(([taskId, statusInfo]) => ({
            fc_id: Number(taskId),
            new_nbd: statusInfo.new_nbd,
            therapy_id: statusInfo.therapy_id,
            ds: statusInfo.ds,
            fill_cycle_number: statusInfo.fill_cycle_number,
          }))
      : [];
    const therapies_with_updated_days_supply = Object.values(fcInclusionStatus || {}).reduce(
      (acc: any, statusInfo) => {
        if (
          statusInfo.status === SL_INCLUSION_STATUS.MOVED_2 &&
          statusInfo.therapy_id &&
          statusInfo.ds &&
          statusInfo.fill_cycle_number
        ) {
          acc.push({
            therapy_id: statusInfo.therapy_id,
            days_supply: statusInfo.ds,
            fill_cycle_number: statusInfo.fill_cycle_number,
          });
        }
        return acc;
      },
      [],
    );
    const payload = {
      patient_id: patient.id,
      fc_task_ids: included_fc_task_ids,
      patient_sms_phone_number: smsPhoneNumber,
      removed_fc_task_info,
    };
    try {
      const response = await SecureFCClient.sendSecureLink(payload);
      setFCssFormIsSubmitting(false);
      const { updatedTasks } = response;
      for (const therapy of therapies_with_updated_days_supply) {
        dispatch(updateTherapyDaysSupply(therapy));
        dispatch(updateFillCycleDaysSupply(therapy));
      }
      dispatch(removeFCOrderTasks());
      dispatch(integrateUpdatedTasks(updatedTasks));
      dispatch(fetchNotes({ patient_id: patient.id }));
      dispatch(notifySuccess('Secure link was sent successfully'));
      dispatch({ type: SET_SHOW_SET_SELECTED_TASKS, payload: false });
    } catch (err: any) {
      if (err.response?.data?.message) {
        dispatch(notifyError(err.response.data.message));
      } else {
        dispatch(notifyError('Error sending secure link'));
      }
    } finally {
      setFCssFormIsSubmitting(false);
      setSendLinkButtonText('Send Link To Patient');
    }
  }

  async function handleResendSecureLink(secure_link_id: number) {
    setFCssFormIsSubmitting(true);
    setResendLinkButtonText('...Resending Link');
    const payload = {
      patient_id: patient.id,
      secure_link_id,
      patient_sms_phone_number: smsPhoneNumber,
    };
    try {
      const response = await SecureFCClient.resendSecureLink(payload);
      dispatch(response);
      dispatch({ type: SET_SHOW_SET_SELECTED_TASKS, payload: false });
    } catch (error) {
      dispatch(notifyError('Error resending secure link'));
    } finally {
      setFCssFormIsSubmitting(false);
      setResendLinkButtonText(RESEND_BUTTON_TEXT);
    }
  }
  const missingFCTaskIds = useMemo(() => {
    return Array.isArray(tasks[0]?.fc_ids_in_bundle) && fcTaskIds
      ? tasks[0].fc_ids_in_bundle
          .filter(item => !fcTaskIds.includes(item))
          .map(id => getTaskKey({ id, taskType: FC }))
      : [];
  }, [fcTaskIds, tasks]);

  const addSelections = useCallback(() => {
    if (isAnOrderTask) {
      const missingOrderTasks: string[] = [];
      const updatedFcInclusionStatus = { ...fcInclusionStatus };
      missingFCTaskIds.forEach((taskId: string) => {
        const missingFCTask = patientTasks[taskId] as IFillCoordinationTask;
        const fc_id = missingFCTask?.id;
        // find the order task that has this fc_id in it's fc_ids field
        const missingOrderTaskKey = Object.keys(patientTasks).find((task_key: any) => {
          const task = patientTasks[task_key];
          if (task.taskType === 'Order' && task.fc_ids?.split(',').includes(fc_id.toString())) {
            return task_key;
          }
        });
        if (missingOrderTaskKey && !missingOrderTasks.includes(missingOrderTaskKey)) {
          missingOrderTasks.push(missingOrderTaskKey);
        }
        updatedFcInclusionStatus[missingFCTask?.id] = {
          status: 1,
          new_nbd: null,
          therapy_id: missingFCTask?.therapy_id || null,
          ds: missingFCTask?.on_hand_qty || null,
          fill_cycle_number: missingFCTask?.fill_cycle_number,
        };
      });

      setFcInclusionStatus?.(updatedFcInclusionStatus);
      dispatch(selectTaskKey(missingOrderTasks));
    } else {
      dispatch(selectTaskKey(missingFCTaskIds));
    }
  }, [dispatch, missingFCTaskIds]);

  async function handleDeactivateSecureLink(secure_link_id: number) {
    const user_response = await new Promise((a, _) => {
      setDeactivateWarningConfig({ show: true, cancel: () => a(false), continue: () => a(true) });
    });
    setDeactivateWarningConfig({ show: false, cancel: () => {}, continue: () => {} });
    if (!user_response) {
      return;
    }
    setFCssFormIsSubmitting(true);
    setDeactiveLinkButtonText('...Processing');
    const payload = { secure_link_id };
    try {
      const response = await SecureFCClient.invalidateSecureLink(payload);
      const { updatedTasks } = response;
      dispatch(integrateUpdatedTasks(updatedTasks));
      dispatch(notifySuccess('Secure Link Deactivated'));
      dispatch({ type: SET_SHOW_SET_SELECTED_TASKS, payload: false });
    } catch (err) {
      dispatch(notifyError('Error Deactivating the Link'));
    } finally {
      setDeactiveLinkButtonText('Deactivate Link');
      setDeactivateWarningConfig({ show: false, cancel: () => {}, continue: () => {} });
      setFCssFormIsSubmitting(false);
    }
  }
  const statusId = startingStatus;
  const secureLinkId = tasks?.find(task => task.secure_link_id)?.secure_link_id;
  const secureLinkNotYetSent = statusId === 4001 || statusId === 4007 || statusId === 4012;
  const secureLinkHasBeenSent = statusId === 4009;
  let tooltipText = '';
  let warningText = '';
  if (!patientIsSMSOptIn) {
    tooltipText = 'Patient must opt-in for SMS messages. ';
  }
  if (includesOnlyFirstTimeFCs) {
    tooltipText += 'FC Selection cannot contain only first-time fills.';
  }
  if (secureLinkSendHistory?.has_limit_violation) {
    warningText =
      'Secure Link unavailable - One or more FCs in this bundle has already had 3 unanswered secure links in the 14 day period of its fill cycle. Please call the patient to complete this bundle. ';

    const over_limit_therapies: any[] = [];
    Object.keys(secureLinkSendHistory.fc_history).forEach(id => {
      if (secureLinkSendHistory.fc_history[id].violation) {
        const therapy = therapiesById[fcsById[id]?.therapy_id];
        if (therapy?.drug_name) {
          over_limit_therapies.push(therapy?.drug_name);
        }
      }
    });
    warningText += `Specific therapies: ${over_limit_therapies.join(', ')}`;
  }

  if (secureLinkNotYetSent) {
    const dispPharm = preferredRxDeliveryPharmacy || mostPrevelantDispensingPharmacyInBundle;
    const dispPharmFull = customerPharmacies.find(pharm => pharm.npi === dispPharm.npi);
    let deliveryConfigNote = 'This pharmacy is configured for courier and UPS delivery';
    if (!dispPharmFull?.is_courier_integrated && !dispPharmFull?.is_ups_integrated) {
      deliveryConfigNote = 'This pharmacy is not configured for UPS or courier delivery';
    } else if (!dispPharmFull?.is_courier_integrated) {
      deliveryConfigNote = 'This pharmacy is not configured for courier delivery';
    } else if (!dispPharmFull?.is_ups_integrated) {
      deliveryConfigNote = 'This pharmacy is not configured for UPS delivery.';
    }
    const needsByDateofTomorrow: any[] = [];
    allTasks.forEach(fc => {
      const a = moment(fc.due_date).format('YYYY-MM-DD');
      const b = moment().add(1, 'days').format('YYYY-MM-DD');
      if ((fc.due_date || '').split('T')[0] === moment().add(1, 'days').format('YYYY-MM-DD')) {
        const therapy = therapiesById[fc.therapy_id];
        if (therapy?.drug_name) {
          needsByDateofTomorrow.push(therapy.drug_name);
        }
      }
    });

    const disable_fc_removal =
      (allTasks || []).reduce((acc, task) => {
        if (fcInclusionStatus?.[task.id] && fcInclusionStatus[task.id].status !== 1) {
          acc += 1;
        }
        return acc;
      }, 0) >=
      (allTasks || []).length - 1;

    return (
      <>
        {containsAClinicAdministeredTherapy ? (
          <Grid item xs={12} className={classes.fieldContainerWithRedTextThin}>
            <div>
              <span className={classes.redCircleExclamationPoint}>!</span>
              This order contains a Clinic Administered medication. Patient will not be able to
              choose pickup or delivery, dates, or signature required status in the secure link
              questionnaire.
            </div>
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Typography className={classes.secureLinkTherapiesListHeader}>
            The following therapies will be included in the secure link:
          </Typography>
        </Grid>
        {allTasks.map(task => {
          return (
            <RenderSimpleTherapyFields
              disable_fc_removal={disable_fc_removal}
              formValues={formValues}
              classes={classes}
              fieldSettings={fieldSettings}
              fcInclusionStatus={fcInclusionStatus}
              isAnOrderTask={isAnOrderTask}
              setFcInclusionStatus={setFcInclusionStatus}
              key={task.id}
              task={task}
              therapy={props.therapies?.[task.therapy_id]}
            />
          );
        })}
        <Grid container item xs={12} className={classes.secureLinkContainerThin}>
          Dispensing Pharmacy for this bundle: {dispPharm.name}
        </Grid>
        <Grid container item xs={12} className={classes.secureLinkContainerThin}>
          {deliveryConfigNote}
        </Grid>
        <Grid container item xs={12} className={classes.secureLinkContainer}>
          {secureLinkSendHistory?.has_limit_violation ? (
            <Grid
              item
              xs={12}
              className={classes.fieldContainerWithRedTextThin}
              style={{ height: 'auto', paddingTop: '5px', paddingBottom: '5px' }}
            >
              <div>
                <span className={classes.redCircleExclamationPoint}>!</span>
                {warningText}
              </div>
            </Grid>
          ) : null}
          {needsByDateofTomorrow.length > 0 ? (
            <Grid item xs={12} className={classes.fieldContainerWithRedTextThin}>
              <div>
                <span className={classes.redCircleExclamationPoint}>!</span>
                One or more therapies in this bundle has a needs-by-date of tomorrow. Please call
                the patient to complete this bundle. Specific Therapies:{' '}
                {needsByDateofTomorrow.join(', ')}
              </div>
            </Grid>
          ) : null}
          <Grid item xs={7}>
            <div>Send a SMS to the patient with a secure link to complete the form.</div>
            <div>This link will be valid until 11:59 pm tomorrow night.</div>
          </Grid>
          <Grid item xs={3} className={classes.secureLinkButton}>
            {!isSecureLinkOptionEnabled ? (
              <IconWithTooltip
                inlineStyle={{ fontSize: '26px' }}
                tooltip={tooltipText}
                type="warning"
              />
            ) : null}
            {needInterpreter && (
              <Tooltip title="SMS will be sent in English" placement="top">
                <WarningIcon className={classes.warningIconSilver} />
              </Tooltip>
            )}

            <Button
              data-qa-id="send-secure-link"
              onClick={() => handleSendSecureLink()}
              variant="contained"
              disabled={
                !isSecureLinkOptionEnabled ||
                fcssFormIsSubmitting ||
                secureLinkSendHistory?.has_limit_violation ||
                needsByDateofTomorrow.length > 0
              }
            >
              {sendLinkButtonText}
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }
  if (secureLinkHasBeenSent) {
    const tasksWithSecureLink = props.allTasks?.filter(task => task.secure_link_id);
    const secure_link_id = tasksWithSecureLink[0].secure_link_id;
    const link_expiration_dt = new ArborDate(
      tasksWithSecureLink[0].secure_link_expiration_dt,
      true,
    ).getCustomerDatetime(true);
    const secure_link_resend_dt = new ArborDate(
      tasksWithSecureLink[0].secure_link_resend_dt,
      true,
    ).getCustomerDatetime(true);
    return (
      <>
        {renderDeactivateLinkConfirmation()}
        {missingFCTaskIds.length ? (
          <Grid item xs={12} className={classes.fieldContainerWithRedText}>
            <div>
              <span className={classes.redCircleExclamationPoint}>!</span>
              {missingFCTaskIds.length === 1
                ? `There is 1 missing ${
                    isAnOrderTask ? 'Order' : 'FC'
                  } that is included in the same secure link bundle.`
                : `There are ${missingFCTaskIds.length} missing ${
                    isAnOrderTask ? 'Orders' : 'FCs'
                  } that are included in the same secure link bundle.`}
            </div>
            <Button
              style={{ color: '#44484C', backgroundColor: 'white', border: '1px solid #C4C4C4' }}
              onClick={addSelections}
            >
              Add Missing Items
            </Button>
          </Grid>
        ) : null}
        <Grid container item xs={12} className={classes.secureLinkContainer}>
          <Grid item xs={4}>
            <div data-secure-link-id={secureLinkId}>
              {`Secure link was sent to the patient by ${secureFCLinkSender}.`}
            </div>
            {secure_link_resend_dt ? (
              <div>{`Secure link resent to patient by Automation on ${secure_link_resend_dt}.`}</div>
            ) : null}
            <div>{`This link will be available until ${link_expiration_dt}.`}</div>
          </Grid>
          <Grid item container xs={3} style={{ justifyContent: 'space-between' }}>
            {secure_link_id ? (
              <Button
                data-qa-id="resend-secure-link"
                onClick={() => handleResendSecureLink(secure_link_id)}
                variant="contained"
                disabled={fcssFormIsSubmitting}
              >
                {resendLinkButtonText}
              </Button>
            ) : null}
          </Grid>
          <Grid item xs={2} className={classes.secureLinkButton}>
            {secure_link_id ? (
              <Button
                data-qa-id="deactivate-secure-link"
                onClick={() => handleDeactivateSecureLink(secure_link_id)}
                disabled={fcssFormIsSubmitting}
                variant="contained"
              >
                {deactivateLinkButtonText}
              </Button>
            ) : null}
          </Grid>
        </Grid>
      </>
    );
  }
  const link_expiration_dt = new ArborDate(
    allTasks?.find(task => task.secure_link_expiration_dt)?.secure_link_expiration_dt,
    true,
  ).getCustomerDatetime(true);
  const secure_link_resend_dt = new ArborDate(
    allTasks?.find(task => task.secure_link_id)?.secure_link_resend_dt,
    true,
  ).getCustomerDatetime(true);
  return (
    <Grid container item xs={12} className={classes.secureLinkContainer}>
      <Grid item xs={7}>
        <div>{`Secure link was sent to the patient by ${secureFCLinkSender}.`}</div>
        {secure_link_resend_dt ? (
          <div>{`Secure link resent to patient by Automation on ${secure_link_resend_dt}.`}</div>
        ) : null}
        <div>{`Secure link was completed by patient ${link_expiration_dt}.`}</div>
      </Grid>
    </Grid>
  );
};
